import { FormInput } from "@/components/form-inputs/form-input";
import { IInput } from "@/components/form-inputs/interfaces";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { generalInputs, lastOrderInputs } from "../../inputs/general-inputs";
import { useStyle } from "../../style";
import { useEffect, useRef } from "react";
import { CUSTOMER_ACTIONS } from "@/pages/customers/enums";
import { useRecoilValue } from "recoil";
import { userState } from "@/store";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { EPreferences } from "@/enums/prefernces";
import type { CustomerRequiredFieldRules } from "@/utils/customer-required-fields";

interface IProps {
  codeFlag: any;
  onChangeInputs: (key: any, value: any) => void;
  customer: any;
  setCustomer: any;
  typeClient: any;
  renderTabPanelTextArea: any;
  languagesOptions: any;
  renderTableSearch: any;
  showTable?: boolean;
  profileState?: any;
  customerAction: CUSTOMER_ACTIONS;
  ShipmentTypesList: { text: string; value: string }[];
  customerRequiredFieldRules: CustomerRequiredFieldRules;
}

const GeneralTab = ({
  codeFlag,
  customer,
  setCustomer,
  onChangeInputs,
  typeClient,
  renderTabPanelTextArea,
  languagesOptions,
  renderTableSearch,
  showTable,
  profileState,
  customerAction,
  ShipmentTypesList,
  customerRequiredFieldRules,
}: IProps) => {
  const { classes } = useStyle();
  const { t } = useTranslation();
  const divRef = useRef(null);
  const user = useRecoilValue<any>(userState);
  const { CheckPreferenceValue } = usePrintHousePreferences();

  useEffect(() => {
    if (divRef.current && showTable) {
      const divElement = divRef.current;
      const lastChild = divElement.lastElementChild;
      if (lastChild) {
        lastChild.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [showTable]);

  useEffect(() => {
    if (!customer?.clientLang && profileState?.systemLang) {
      setCustomer((prev) => ({
        ...prev,
        clientLang: profileState.systemLang,
      }));
    }
  }, [customer?.clientLang, profileState?.systemLang]);

  useEffect(() => {
    if (customerAction === CUSTOMER_ACTIONS.Add) {
      setCustomer((prev) => ({
        ...prev,
        isActive: true,
      }));
    }
  }, [customerAction]);

  const autoAssignAgent = () => {
    if (
      customerAction === CUSTOMER_ACTIONS.Add &&
      user?.isAgent &&
      CheckPreferenceValue(EPreferences.SET_CURRENT_USER_AS_CLIENT_AGENT)
    ) {
      setCustomer((prev) => ({
        ...prev,
        agentId: user?.employeeId,
      }));
    }
  };

  useEffect(() => {
    autoAssignAgent();
  }, [customerAction]);

  return (
    <div style={classes.tabsContainer} ref={divRef}>
      <div style={classes.customerInfoStyle}>
        {generalInputs(
          typeClient,
          customer,
          languagesOptions,
          t,
          ShipmentTypesList,
          customerRequiredFieldRules,
        )
          .filter(
            (item) =>
              !!CheckPreferenceValue(
                EPreferences.SHOW_CUSTOMER_SUPPORT_REPRESENTATIVE,
              ) || item.parameterKey !== "customerSupportRepresentativeId",
          )
          .map((item) => (
          <div style={{ marginBottom: 10 }}>
            <FormInput
              key={"general-input"}
              input={item as IInput}
              changeState={onChangeInputs}
              error={false}
              readonly={false}
            />
          </div>
        ))}
      </div>
      <div>
        <Stack
          direction={"row"}
          marginTop={"8px"}
          marginBottom={"8px"}
          gap="20px"
        >
          {renderTabPanelTextArea(
            t("customers.modal.generalComment"),
            customer?.generalNotes,
            (e) =>
              setCustomer((prev) => ({
                ...prev,
                generalNotes: e.target.value,
              }))
          )}
          {renderTabPanelTextArea(
            t("customers.modal.orderOpeningNotes"),
            customer?.newItemNotes,
            (e) =>
              setCustomer((prev) => ({
                ...prev,
                newItemNotes: e.target.value,
              }))
          )}
          {renderTabPanelTextArea(
            t("customers.modal.orderClosingNotes"),
            customer?.closeOrderNotes,
            (e) =>
              setCustomer((prev) => ({
                ...prev,
                closeOrderNotes: e.target.value,
              }))
          )}
        </Stack>
        {codeFlag && (
          <>
            <Stack direction={"row"}>
              <span style={classes.lastOrderDetails}>
                {t("customers.modal.lastOrderDetails")}
              </span>
            </Stack>
            <div style={classes.customerInfoStyle}>
              {lastOrderInputs(customer).map((item) => (
                <div style={{ marginBottom: 10 }}>
                  <FormInput
                    key={"last-order-details-input"}
                    input={item as IInput}
                    changeState={onChangeInputs}
                    error={false}
                    readonly={!!item.readonly}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {renderTableSearch}
    </div>
  );
};

export { GeneralTab };
